const META_READ_TIMEOUT_MS = 15000;
const META_READ_RETRIES = 2;

/**
 * Reads duration/resolution straight out of an MP4/MOV container's
 * moov box (mvhd + the first video trak's tkhd), without decoding any
 * video frames. Used as a fallback when the browser's <video> element
 * fails to fire `loadedmetadata` (e.g. a busy/unavailable hardware
 * decoder) even though the file itself is perfectly valid.
 */
const parseMp4ContainerMeta = (
  buffer: ArrayBuffer
): { duration: number; resolution: string } | null => {
  try {
    const view = new DataView(buffer);
    const total = buffer.byteLength;

    const readBoxHeader = (offset: number) => {
      if (offset + 8 > total) return null;
      const size = view.getUint32(offset);
      const type = String.fromCharCode(
        view.getUint8(offset + 4),
        view.getUint8(offset + 5),
        view.getUint8(offset + 6),
        view.getUint8(offset + 7)
      );
      let boxSize = size;
      let headerSize = 8;
      if (size === 1) {
        if (offset + 16 > total) return null;
        boxSize = Number(view.getBigUint64(offset + 8));
        headerSize = 16;
      } else if (size === 0) {
        boxSize = total - offset;
      }
      if (boxSize < headerSize || offset + boxSize > total) return null;
      return { type, contentStart: offset + headerSize, boxEnd: offset + boxSize };
    };

    const findBox = (type: string, start: number, end: number) => {
      let offset = start;
      while (offset < end) {
        const header = readBoxHeader(offset);
        if (!header) return null;
        if (header.type === type) return header;
        offset = header.boxEnd;
      }
      return null;
    };

    const moov = findBox('moov', 0, total);
    if (!moov) return null;

    const mvhd = findBox('mvhd', moov.contentStart, moov.boxEnd);
    if (!mvhd) return null;

    const mvhdVersion = view.getUint8(mvhd.contentStart);
    let p = mvhd.contentStart + 4;
    let timescale: number;
    let duration: number;
    if (mvhdVersion === 1) {
      p += 16;
      timescale = view.getUint32(p);
      p += 4;
      duration = Number(view.getBigUint64(p));
    } else {
      p += 8;
      timescale = view.getUint32(p);
      p += 4;
      duration = view.getUint32(p);
    }
    if (!timescale) return null;

    let width = 0;
    let height = 0;
    let trakOffset = moov.contentStart;
    while (trakOffset < moov.boxEnd) {
      const trakHeader = readBoxHeader(trakOffset);
      if (!trakHeader) break;
      if (trakHeader.type === 'trak') {
        const mdia = findBox('mdia', trakHeader.contentStart, trakHeader.boxEnd);
        const hdlr = mdia && findBox('hdlr', mdia.contentStart, mdia.boxEnd);
        const handlerType = hdlr
          ? String.fromCharCode(
              view.getUint8(hdlr.contentStart + 8),
              view.getUint8(hdlr.contentStart + 9),
              view.getUint8(hdlr.contentStart + 10),
              view.getUint8(hdlr.contentStart + 11)
            )
          : null;
        if (handlerType === 'vide') {
          const tkhd = findBox('tkhd', trakHeader.contentStart, trakHeader.boxEnd);
          if (tkhd) {
            const tkhdVersion = view.getUint8(tkhd.contentStart);
            let tp = tkhd.contentStart + 4;
            tp += tkhdVersion === 1 ? 8 + 8 + 4 + 4 + 8 : 4 + 4 + 4 + 4 + 4;
            tp += 8 + 2 + 2 + 2 + 2 + 36; // reserved, layer, alt group, volume, reserved, matrix
            width = view.getUint32(tp) / 65536;
            height = view.getUint32(tp + 4) / 65536;
          }
          break;
        }
      }
      trakOffset = trakHeader.boxEnd;
    }

    if (!width || !height) return null;

    return {
      duration: parseFloat((duration / timescale).toFixed(2)),
      resolution: `${Math.round(width)}x${Math.round(height)}`,
    };
  } catch {
    return null;
  }
};

const withRetry = async <T>(
  attempt: () => Promise<T>,
  retries: number
): Promise<T> => {
  try {
    return await attempt();
  } catch (err) {
    if (retries <= 0) throw err;
    return withRetry(attempt, retries - 1);
  }
};

const readVideoMetaOnce = (
  file: File
): Promise<{ duration: number; resolution: string }> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out reading video metadata'));
    }, META_READ_TIMEOUT_MS);

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (settled) return;
      settled = true;
      const res = `${video.videoWidth}x${video.videoHeight}`;
      const dur = !isNaN(video.duration)
        ? parseFloat(video.duration.toFixed(2))
        : 0;
      cleanup();
      resolve({ duration: dur, resolution: res });
    };
    video.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Failed to read video metadata'));
    };
    video.src = objectUrl;
  });

const readImageMetaOnce = (file: File): Promise<{ resolution: string }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out reading image metadata'));
    }, META_READ_TIMEOUT_MS);

    img.onload = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ resolution: `${img.width}x${img.height}` });
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Failed to read image metadata'));
    };
    img.src = objectUrl;
  });

/**
 * Reads video metadata (duration, resolution) from a File.
 * Retries a couple of times before giving up on the browser's <video>
 * element, since a single failed decode attempt (e.g. the hardware
 * video decoder briefly unavailable) shouldn't reject an otherwise
 * valid file. If every decode attempt still fails, falls back to
 * reading duration/resolution directly out of the MP4/MOV container
 * (no decoding required) before concluding the file is actually bad.
 */
export const getVideoMeta = async (
  file: File
): Promise<{ duration: number; resolution: string }> => {
  try {
    return await withRetry(() => readVideoMetaOnce(file), META_READ_RETRIES);
  } catch (err) {
    const isMp4Like =
      file.type === 'video/mp4' ||
      file.type === 'video/quicktime' ||
      /\.(mp4|mov|m4v)$/i.test(file.name);
    if (isMp4Like) {
      const containerMeta = parseMp4ContainerMeta(await file.arrayBuffer());
      if (containerMeta) return containerMeta;
    }
    throw err;
  }
};

/**
 * Reads image metadata (resolution) from a File.
 */
export const getImageMeta = (file: File): Promise<{ resolution: string }> =>
  withRetry(() => readImageMetaOnce(file), META_READ_RETRIES);

export const validateFileSizes = (
  files: File[],
  limits: { imageMaxBytes: number; videoMaxBytes: number }
): { validFiles: File[]; oversizedFilenames: string[] } => {
  const oversizedFilenames: string[] = [];

  const validFiles = files.filter(file => {
    const isVideo = file.type.startsWith('video/');
    const maxBytes = isVideo ? limits.videoMaxBytes : limits.imageMaxBytes;
    if (file.size > maxBytes) {
      oversizedFilenames.push(file.name);
      return false;
    }
    return true;
  });

  return { validFiles, oversizedFilenames };
};
